import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MapContainer, GeoJSON, useMap, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.vectorgrid';
import 'maplibre-gl/dist/maplibre-gl.css';
import { setWorkerUrl } from 'maplibre-gl';
import { maplibreGL } from '@maplibre/maplibre-gl-leaflet';
import './Map.css';
import PropertyLinesLayer from './PropertyLinesLayer';
import { config } from '../../utils/config';
import { patchLeafletVectorGridFetch } from '../../utils/leafletVectorGridPatch';

patchLeafletVectorGridFetch();

// MapLibre v6 needs an explicit worker URL when bundled by Webpack/CRA.
// The prebuild/prestart hook copies both required ESM files to this public path.
setWorkerUrl('/maplibre/maplibre-gl-worker.mjs');

// Fix marker icons
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Fix for Leaflet marker icons - using require instead of import for image files
const iconUrl = require('leaflet/dist/images/marker-icon.png');
const iconRetinaUrl = require('leaflet/dist/images/marker-icon-2x.png');
const shadowUrl = require('leaflet/dist/images/marker-shadow.png');

const defaultIcon = L.icon({
  iconUrl,
  iconRetinaUrl,
  shadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = defaultIcon;

// Extend the global Window interface
declare global {
  interface Window {
    mapInstance?: L.Map;
    L?: typeof L;
  }
}

// Make Leaflet available globally
if (typeof window !== 'undefined') {
  window.L = L;
}

interface MapProps {
  onZoneClick: (properties: any, parcelId?: string) => void;
  onAddressSelect?: (handler: (lat: number, lng: number, displayName: string) => string | undefined) => void;
  internalMapRef?: (map: L.Map) => void;
  showPropertyLines?: boolean;
  parcelData?: any;
  onZoneFinderReady?: (finder: (lat: number, lng: number) => any | null) => void;
  selectedZone?: any;
  displayUnit?: 'm' | 'ft';
  // Progressive loading props for mobile
  progressiveZoningData?: any;
  progressiveBoundaryData?: any;
  isProgressiveLoading?: boolean;
  progressiveLoadingProgress?: number;
  // Mobile layout state to ensure legend matches sidebar behavior
  isMobileLayout?: boolean;
}

/**
 * Minimal vector basemap without embedded business or restaurant symbols.
 * OpenFreeMap's public Positron style is key-free, while this Leaflet bridge
 * keeps the existing zoning, property, boundary, and marker layers unchanged.
 */
const PremiumBasemap: React.FC = () => {
  const map = useMap();

  useEffect(() => {
    // Keep a muted raster map underneath until the vector style and its first
    // visible tiles are ready. It also becomes the automatic fallback when
    // WebGL or the external vector service is unavailable.
    const fallbackLayer = L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
      minZoom: 8,
      maxZoom: 20,
      maxNativeZoom: 19,
      className: 'zoningpal-basemap-fallback',
      crossOrigin: true
    }).addTo(map);

    let maplibreLayer: L.MaplibreGL | null = null;
    let fallbackTimer: number | undefined;
    let vectorMap: ReturnType<L.MaplibreGL['getMaplibreMap']> | null = null;
    let vectorReady = false;

    const keepFallback = () => {
      if (vectorReady) return;
      if (maplibreLayer && map.hasLayer(maplibreLayer)) {
        map.removeLayer(maplibreLayer);
      }
    };

    const useVectorMap = () => {
      if (!vectorMap || vectorReady) return;
      vectorReady = true;
      if (fallbackTimer !== undefined) window.clearTimeout(fallbackTimer);
      if (map.hasLayer(fallbackLayer)) map.removeLayer(fallbackLayer);
    };

    try {
      maplibreLayer = maplibreGL({
        style: 'https://tiles.openfreemap.org/styles/positron',
        interactive: false,
        renderWorldCopies: false
      });

      maplibreLayer.addTo(map);
      vectorMap = maplibreLayer.getMaplibreMap();
      vectorMap.on('load', useVectorMap);
      fallbackTimer = window.setTimeout(keepFallback, 20000);
    } catch (error) {
      // The muted raster layer is already visible, so a WebGL failure never
      // leaves the zoning map empty.
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('Premium vector basemap unavailable; using fallback.', error);
      }
      keepFallback();
    }

    return () => {
      if (fallbackTimer !== undefined) window.clearTimeout(fallbackTimer);
      vectorMap?.off('load', useVectorMap);
      if (maplibreLayer && map.hasLayer(maplibreLayer)) map.removeLayer(maplibreLayer);
      if (map.hasLayer(fallbackLayer)) map.removeLayer(fallbackLayer);
    };
  }, [map]);

  return null;
};

const TorontoMap: React.FC<MapProps> = ({ 
  onZoneClick, 
  onAddressSelect, 
  internalMapRef: internalMapRefProp, 
  showPropertyLines = false, 
  parcelData, 
  onZoneFinderReady,
  selectedZone,
  displayUnit = 'm',
  // Progressive loading props
  progressiveZoningData,
  progressiveBoundaryData,
  isProgressiveLoading = false,
  progressiveLoadingProgress = 0,
  // Mobile layout state
  isMobileLayout = false
}) => {
  // Property lines are visible by default (but only load at high zoom levels)
  const [propertyLinesVisible] = useState<boolean>(true);
  // State
  const [cityBoundary, setCityBoundary] = useState<GeoJSON.FeatureCollection | null>(null);
  const [formerCityAreas] = useState<boolean>(true); // Toggle for former city areas
  const [loading, setLoading] = useState<boolean>(true);

  const [error] = useState<string | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars  
  const [searchLocation, setSearchLocation] = useState<[number, number] | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [searchDisplayName, setSearchDisplayName] = useState<string>('');
  const [isLegendOpen, setIsLegendOpen] = useState(false);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  
  // Ref for legend panel to detect clicks outside
  const legendPanelRef = useRef<HTMLDivElement>(null);
  // Removed unused vectorZoningLoaded state

  
  // Refs
  const internalMapRef = useRef<L.Map | null>(null);
  const searchMarkerRef = useRef<L.Marker | null>(null);
  const pulseCircleRef = useRef<L.Circle | null>(null);
  const zoningVectorLayerRef = useRef<any>(null);
  const formerCityVectorLayerRef = useRef<any>(null);
  
  // Forward map reference to parent component when it's available
  useEffect(() => {
    if (internalMapRef.current && internalMapRefProp) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('Forwarding map reference to App component');
      }
      internalMapRefProp(internalMapRef.current);
    }
  }, [internalMapRefProp]); // Remove mutable ref from dependencies

  // Setup function for any map initialization
  const setupMap = (map: L.Map) => {
    // Store the map reference
    internalMapRef.current = map;
    
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.log('Map setup completed, current zoom:', map.getZoom());
    }
    
    // Make the map available to parent components if needed
    if (internalMapRefProp) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('Passing map reference to parent component');
      }
      internalMapRefProp(map);
    }
    
    // Add zoom event listener for debugging
    map.on('zoomend', () => {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('Map zoom changed to:', map.getZoom());
      }
    });
    
    // Simple pane setup
    map.createPane('formerCityPane');
    map.getPane('formerCityPane')!.style.zIndex = '1200'; // Above main zoning layer

    // Keep the municipal boundary above zoning canvases, but below markers and
    // controls. Separate panes let the pale halo and blue core stay crisp at
    // every zoom level without intercepting map interaction.
    map.createPane('cityBoundaryHaloPane');
    map.getPane('cityBoundaryHaloPane')!.style.zIndex = '580';
    map.getPane('cityBoundaryHaloPane')!.style.pointerEvents = 'none';
    map.createPane('cityBoundaryPane');
    map.getPane('cityBoundaryPane')!.style.zIndex = '585';
    map.getPane('cityBoundaryPane')!.style.pointerEvents = 'none';
    
    // Also make it available globally for debugging
    if (typeof window !== 'undefined') {
      (window as any).mapInstance = map;
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('Map instance made available globally as window.mapInstance');
      }
    }
  };

  // Style function for zoning features
  const styleFeature = (feature: GeoJSON.Feature<GeoJSON.Geometry, any> | undefined): L.PathOptions => {
    if (!feature || !feature.properties) return { fillColor: '#CCCCCC', weight: 1, opacity: 0.8, color: '#444444', fillOpacity: 0.25 };
    
    // Get the full zoning code
    const zoneCode = feature.properties.ZN_ZONE || '';
    const zoneLabel = feature.properties.ZN_STRING || feature.properties.DESCRIPTION || '';
    
    // Set color based on zone type
    let fillColor = '#CCCCCC'; // Default gray
    
    // Check if this is a Former City of Toronto By-law area
    // This can be identified by specific properties in your GeoJSON
    // Adjust these conditions based on your actual data structure
    if (formerCityAreas && 
        (zoneLabel.includes('Former City of Toronto') || 
         zoneLabel.includes('Subject to Former') || 
         (zoneCode === 'ON' && zoneLabel.includes('Open Space')) || 
         zoneCode === '')) {
      // Use a distinctive pattern/color for Former City of Toronto By-law areas
      return {
        fillColor: '#E8E8E8', // Light grey color
        fillOpacity: 0.5,
        weight: 1,
        color: '#999999', // Medium grey border
        dashArray: '3, 3', // Dashed border for distinction
        className: 'former-bylaw-area'
      };
    }
    
    // Check for specific zone types first. Order matters – check more specific prefixes before broader ones.
    if (zoneCode.startsWith('CRE')) {
      fillColor = '#8B4513'; // Commercial Residential Employment (CRE) - Brown
    } else if (zoneCode.startsWith('CR')) {
      fillColor = '#FF0000'; // Commercial Residential (CR) - Red
    } else if (zoneCode.startsWith('RA')) {
      fillColor = '#FFA500'; // Residential Apartment (RA) - Orange
    } else {
      // For other zones, use the first character
      const zoneType = zoneCode.charAt(0);
      
      switch(zoneType) {
        case 'R': // Residential
          fillColor = '#FFD700'; // Gold (more vibrant yellow)
          break;
        case 'C': // Commercial
          fillColor = '#FF1493'; // Deep Pink (more vibrant)
          break;
        case 'E': // Employment/Industrial
          fillColor = '#9400D3'; // Dark Violet (more vibrant purple)
          break;
        case 'I': // Institutional
          fillColor = '#1E90FF'; // Dodger Blue (more vibrant blue)
          break;
        case 'O': // Open Space
          fillColor = '#32CD32'; // Lime Green (more vibrant green)
          break;
        case 'U': // Utility
          fillColor = '#808080'; // Gray
          break;
        default:
          fillColor = '#CCCCCC'; // Default gray
      }
    }
    
    return {
      fillColor,
      weight: 1,
      opacity: 0.8,
      color: '#444444',
      fillOpacity: 0.25  // Further lowered opacity as requested
    };
  };
  
  // Try to add vector-tile zoning layer (keeps existing GeoJSON fallback)
  const addZoningVectorLayer = useCallback(() => {
    if (!internalMapRef.current || zoningVectorLayerRef.current) {
      return;
    }
    

    
    if (!(L as any).vectorGrid) {
      console.error('❌ Leaflet.vectorGrid not available!');
      return;
    }
    
    try {
      // @ts-ignore - vectorGrid typings not provided
      const layer = (L as any).vectorGrid.protobuf(
        `${config.TILES_URL}/data/zoning/{z}/{x}/{y}.pbf`,
        {
          vectorTileLayerStyles: {
            toronto_zoning: (properties: any, zoom: number) => {
              if (process.env.NODE_ENV !== 'production') {
                console.log('🎨 Vector tile styling called for:', properties, 'at zoom:', zoom);
              }
              
              // Create a fake feature object that matches what styleFeature expects
              const fakeFeature = {
                properties: {
                  ZN_ZONE: properties.ZN_ZONE || '',
                  ZN_STRING: properties.ZN_STRING || properties.DESCRIPTION || ''
                }
              };
              
              const style = styleFeature(fakeFeature as any);
              
              const vectorStyle = {
                fill: true,
                fillColor: style.fillColor || '#CCCCCC',
                fillOpacity: 0.25, // Use proper opacity
                stroke: false, // Remove stroke outline
                color: 'transparent', // No border color
                weight: 0, // No border weight
                opacity: 0 // No border opacity
              };
              
              if (process.env.NODE_ENV !== 'production') {
                console.log('🎨 Vector tile style applied:', vectorStyle);
              }
              
              return vectorStyle;
            },
          },
          tileSize: 256, // Match the tile generation size
          maxZoom: 20, // Higher zoom support
          minZoom: 8,
          maxNativeZoom: 16, // Our tiles go to zoom 16
          rendererFactory: (L as any).canvas.tile, // Canvas is faster for solid colors
          interactive: false,
          pane: 'overlayPane',
          zIndex: 1000,
          getFeatureId: (f: any) => f.properties.id || f.id,
          // Light caching settings
          updateWhenZooming: false,
          updateWhenIdle: true
        }
      );
      layer.on('tileerror', (e: any) => {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Vector tile failed to load:', e);
        }
        // Vector tiles are stable now - no fallback needed
      });
      layer.on('tileload', (e: any) => {
        setLoading(false); // Hide loading when tiles start appearing
        if (process.env.NODE_ENV !== 'production') {
          console.log('Vector tile loaded successfully:', e.coords, 'URL:', e.url);
        }
      });
      
      layer.on('tileloadstart', (e: any) => {
        if (process.env.NODE_ENV !== 'production') {
          console.log('Vector tile load started:', e.coords, 'URL:', e.url);
        }
      });
      
      // Disable vector tile clicks for now to prevent errors
      // Click handling will fall through to the map click handler
      
      layer.addTo(internalMapRef.current);
      zoningVectorLayerRef.current = layer;
      

    } catch (e) {
      console.error('Vector tile layer construction failed:', e);
      // Vector tiles are stable now - no fallback needed
    }
  }, [styleFeature]); // Include styleFeature dependency

  // Add former city zoning vector layer
  const addFormerCityVectorLayer = useCallback(() => {
    if (!internalMapRef.current || formerCityVectorLayerRef.current) return;

    if (process.env.NODE_ENV !== 'production') {
      console.log('🏛️ Adding former city zoning vector layer...');
    }

    try {
      // @ts-ignore - vectorGrid typings not provided
      const layer = (L as any).vectorGrid.protobuf(
        `${config.TILES_URL}/data/former-city/{z}/{x}/{y}.pbf`,
        {
          vectorTileLayerStyles: {
            former_city_zoning: (properties: any, zoom: number) => {
              if (process.env.NODE_ENV !== 'production') {
                console.log('🏛️ Former city tile styling called for:', properties, 'at zoom:', zoom);
              }
              
              // Style for former city areas - clean light grey
              const vectorStyle = {
                fill: true,
                fillColor: '#E0E0E0', // Light grey for former city areas
                fillOpacity: 0.4, // Subtle transparency
                stroke: false, // No stroke outline
                color: 'transparent', // No border color
                weight: 0, // No border weight
                opacity: 0, // No border opacity
                className: 'former-city-zone'
              };
              
              if (process.env.NODE_ENV !== 'production') {
                console.log('🏛️ Former city style applied:', vectorStyle);
              }
              
              return vectorStyle;
            },
          },
          tileSize: 256,
          maxZoom: 20, // Higher zoom support
          minZoom: 8,
          maxNativeZoom: 16, // Our tiles go to zoom 16
          rendererFactory: (L as any).canvas.tile, // Canvas is faster for solid colors
          interactive: false,
          pane: 'formerCityPane',
          zIndex: 1200, // Above main zoning layer
          getFeatureId: (f: any) => f.properties.id || f.id,
          updateWhenZooming: false,
          updateWhenIdle: true
        }
      );

      layer.on('tileerror', (e: any) => {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('🏛️ Former city tile error:', e);
        }
      });

      layer.on('tileload', () => {
        if (process.env.NODE_ENV !== 'production') {
          console.log('🏛️ Former city tile loaded successfully');
        }
      });

      layer.addTo(internalMapRef.current);
      formerCityVectorLayerRef.current = layer;
      
      if (process.env.NODE_ENV !== 'production') {
        console.log('✅ Former city vector layer added successfully');
      }

    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('❌ Failed to add former city vector layer:', error);
      }
    }
  }, []);

  // Enable vector tiles immediately - no delays
  useEffect(() => {
    addZoningVectorLayer();
    // Former city layer removed per user request
  }, [addZoningVectorLayer]);

  // Define styles for map layers
  const cityBoundaryHaloStyle = (): L.PathOptions => {
    return {
      color: '#f8fafc',
      weight: 7,
      opacity: 0.9,
      fillOpacity: 0,
      fill: false,
      lineCap: 'round',
      lineJoin: 'round'
    };
  };

  const cityBoundaryStyle = (): L.PathOptions => {
    return {
      color: '#1d4ed8',
      weight: 3.5,
      opacity: 1,
      fillOpacity: 0,
      fill: false,
      lineCap: 'round',
      lineJoin: 'round'
    };
  };
  
  // Style for unzoned areas - transparent background, no fill
  const unzonedAreaStyle = (): L.PathOptions => {
    return {
      fillColor: 'transparent', // No background fill
      fillOpacity: 0, // No fill opacity
      weight: 0, // No border
      color: 'transparent', // No border color
      fill: false, // Explicitly disable fill
      className: 'unzoned-area'
    };
  };

  // Legacy GeoJSON loading removed - vector tiles are stable now
  
  // Load the Toronto boundary from the extracted GeoJSON file
  const loadTorontoBoundary = async () => {
    try {
      const response = await fetch(`${config.TILES_URL}/data/toronto_boundary_extracted.json`);
      if (!response.ok) {
        throw new Error(`Failed to load Toronto boundary: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      setCityBoundary(data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error loading Toronto boundary:', error);
    }
  };

  // Create an initial visibility state but default to visible for reliability
  const [mapReady, setMapReady] = useState<boolean>(true); // Default to true to ensure map is visible

  // Simple fast loading - no artificial delays
  useEffect(() => {
    loadTorontoBoundary();
    setMapReady(true);
  }, []);
  
  // Listen for the global pre-render complete signal
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.log('Checking for map pre-render complete signal');
    }
    // Always make map visible after a short delay
    setTimeout(() => {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('Forcing map to be visible');
      }
      setMapReady(true);
    }, 300);
  }, []);

  // Handle feature click events
  const onEachFeature = (feature: GeoJSON.Feature | undefined, layer: L.Layer) => {
    if (!feature) return;
    
    layer.on({
      click: () => {
        if (feature.properties) {
          // Extract PARCELI2 from feature properties if available
          const parcelId = feature.properties.PARCELI2 || null;
          // Log for debugging
          if (parcelId && process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.log(`Clicked on parcel with PARCELI2: ${parcelId}`);
          }
          onZoneClick(feature.properties, parcelId);
        }
      }
    });
  };

  // Handle address selection from search
  const handleAddressSelect = (lat: number, lng: number, displayName: string) => {
    setSearchLocation([lat, lng]);
    setSearchDisplayName(displayName);
    
    // Variable to store parcelId if found
    let parcelId: string | undefined;
    
    // If map is available, update the view
    if (internalMapRef.current) {
      // Note: Zoom level is controlled by App component, not here
      // internalMapRef.current.setView([lat, lng], 16); // REMOVED - App handles zoom
      
      // Remove existing marker if any
      if (searchMarkerRef.current) {
        searchMarkerRef.current.remove();
      }
      
      // Remove existing pulse circle if any
      if (pulseCircleRef.current) {
        pulseCircleRef.current.remove();
      }
      
      // Add a marker at the selected location
      const marker = L.marker([lat, lng]).addTo(internalMapRef.current);
      searchMarkerRef.current = marker;
      
      // Add a pulsing circle
      const pulseCircle = L.circle([lat, lng], {
        radius: 50,
        color: '#3388ff',
        fillColor: '#3388ff',
        fillOpacity: 0.15,
        weight: 2,
        className: 'pulse-circle'
      }).addTo(internalMapRef.current);
      pulseCircleRef.current = pulseCircle;
      
      // Animate the pulse circle
      const animatePulse = () => {
        if (pulseCircleRef.current) {
          pulseCircleRef.current.setStyle({ fillOpacity: 0.08 });
          
          setTimeout(() => {
            if (pulseCircleRef.current) {
              pulseCircleRef.current.setStyle({ fillOpacity: 0.2 });
              
              setTimeout(() => {
                if (pulseCircleRef.current) {
                  pulseCircleRef.current.setStyle({ fillOpacity: 0.08 });
                }
                requestAnimationFrame(animatePulse);
              }, 500);
            }
          }, 500);
        }
      };
      
      animatePulse();
      
      // Try to find the parcel ID at this location
      try {
        // Query all layers at this point to find parcel information
        internalMapRef.current.eachLayer((layer: any) => {
          if (layer.feature && layer.feature.properties && layer.feature.properties.PARCELI2) {
            // Check if the point is inside this layer
            const point = L.latLng(lat, lng);
            if (layer.getBounds && layer.getBounds().contains(point)) {
              parcelId = layer.feature.properties.PARCELI2;
              if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.log(`Found parcel ID from address search: ${parcelId}`);
              }
            }
          }
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error finding parcel ID at search location:', error);
      }
    }
  };
  
  // Map controller component to access the map context
  const MapController = () => {
    const map = useMap();
    
    useEffect(() => {
      // Initialize the map
      setupMap(map);
      
      // Setup address selection handler if provided
      if (onAddressSelect) {
        // Create a handler that will be called when an address is selected
        const addressHandler = (lat: number, lng: number, displayName: string) => {
          // Call our internal handler first to set up the UI and find the parcel ID
          handleAddressSelect(lat, lng, displayName);
          
          // Try to find parcel ID at this location
          let foundParcelId: string | undefined;
          try {
            map.eachLayer((layer: any) => {
              if (layer.feature && layer.feature.properties && layer.feature.properties.PARCELI2) {
                // Check if the point is inside this layer
                const point = L.latLng(lat, lng);
                if (layer.getBounds && layer.getBounds().contains(point)) {
                  foundParcelId = layer.feature.properties.PARCELI2;
                }
              }
            });
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error finding parcel ID in wrapped handler:', error);
          }
          
          // Return the parcel ID if found
          return foundParcelId;
        };
        
        // Pass our handler to the parent component
        onAddressSelect(addressHandler);
      }
    }, [map]);

    // Handle orientation changes and screen resizing to fix map glitches
    useEffect(() => {
      let resizeTimeout: NodeJS.Timeout;
      let orientationTimeout: NodeJS.Timeout;

      const handleResize = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          console.log('🗺️ Map: Handling screen resize');
          
          // Force map to recalculate its size and center
          map.invalidateSize({ 
            animate: false, // No animation to prevent glitches
            pan: false      // Don't change the center
          });
          
          // Small delay to ensure DOM has updated
          setTimeout(() => {
            map.invalidateSize({ 
              animate: true,  // Smooth animation for final adjustment
              pan: false 
            });
          }, 100);
        }, 150); // Debounce rapid resize events
      };

      const handleOrientationChange = () => {
        clearTimeout(orientationTimeout);
        orientationTimeout = setTimeout(() => {
          console.log('🗺️ Map: Handling orientation change - fixing tile corruption');
          
          // More aggressive invalidation for orientation changes
          // which can cause more severe layout issues
          map.invalidateSize({ animate: false, pan: false });
          
          // Force a repaint of all vector layers to fix corruption
          if (zoningVectorLayerRef.current) {
            console.log('🔄 Redrawing zoning vector layer');
            zoningVectorLayerRef.current.redraw();
          }
          if (formerCityVectorLayerRef.current) {
            console.log('🔄 Redrawing former city vector layer');
            formerCityVectorLayerRef.current.redraw();
          }
          
          // Final smooth adjustment
          setTimeout(() => {
            map.invalidateSize({ animate: true, pan: false });
            console.log('✅ Map orientation change handling complete');
          }, 200);
        }, 300); // Longer delay for orientation changes
      };

      // Listen for both resize and orientation change events
      window.addEventListener('resize', handleResize, { passive: true });
      window.addEventListener('orientationchange', handleOrientationChange, { passive: true });

      return () => {
        clearTimeout(resizeTimeout);
        clearTimeout(orientationTimeout);
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('orientationchange', handleOrientationChange);
      };
    }, [map]);
    
    return null;
  };
  
  // Helper function to calculate feature area (rough approximation)
  const calculateFeatureArea = useCallback((feature: GeoJSON.Feature): number => {
    if (!feature.geometry) return Infinity;
    
    try {
      if (feature.geometry.type === 'Polygon') {
        const coordinates = feature.geometry.coordinates[0];
        return calculatePolygonArea(coordinates);
      } else if (feature.geometry.type === 'MultiPolygon') {
        const polygons = feature.geometry.coordinates;
        return polygons.reduce((total, polygon) => {
          return total + calculatePolygonArea(polygon[0]);
        }, 0);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error calculating area:', error);
    }
    
    return Infinity;
  }, []);

  // Helper function to calculate polygon area
  const calculatePolygonArea = (coordinates: number[][]): number => {
    if (coordinates.length < 3) return 0;
    
    let area = 0;
    for (let i = 0; i < coordinates.length; i++) {
      const j = (i + 1) % coordinates.length;
      area += coordinates[i][0] * coordinates[j][1];
      area -= coordinates[j][0] * coordinates[i][1];
    }
    return Math.abs(area) / 2;
  };
  
  // Function to find zoning information at coordinates using API call
  const findZoneAtCoordinates = useCallback((lat: number, lng: number, callback?: (result: any) => void): any => {
    if (process.env.NODE_ENV !== 'production') {
      console.log(`Finding zone at coordinates via API: ${lat}, ${lng}`);
    }
    
    // If callback provided, handle async
    if (callback) {
      (async () => {
        try {
          const response = await fetch(`${config.API_URL}/api/zoning/spatial-data?lat=${lat}&lng=${lng}`);
          
          if (!response.ok) {
            console.error('Spatial API error:', response.status, response.statusText);
            callback(null);
            return;
          }
          
          const spatialData = await response.json();
          
          if (spatialData && spatialData.zone) {
            if (process.env.NODE_ENV !== 'production') {
              console.log('Zone found via API:', spatialData.zone);
            }
            
            // Convert API response to format expected by the UI
            const result = {
              ZN_ZONE: spatialData.zone.base_zone || spatialData.zone.zone_code || '',
              ZN_STRING: spatialData.zone.zone_label || '',
              DESCRIPTION: spatialData.zone.zone_label || '',
              // Include other useful properties
              PARCELI2: spatialData.parcel?.parceli2 || null,
              ADDRESS: spatialData.parcel?.address_full || null
            };
            callback(result);
          } else {
            callback(null);
          }
        } catch (error) {
          console.error('Error fetching zone data from API:', error);
          callback(null);
        }
      })();
      
      // Return null immediately for sync compatibility
      return null;
    }
    
    // For backward compatibility, return a promise
    return fetch(`${config.API_URL}/api/zoning/spatial-data?lat=${lat}&lng=${lng}`)
      .then(response => response.ok ? response.json() : null)
      .then(spatialData => {
        if (spatialData && spatialData.zone) {
          return {
            ZN_ZONE: spatialData.zone.base_zone || spatialData.zone.zone_code || '',
            ZN_STRING: spatialData.zone.zone_label || '',
            DESCRIPTION: spatialData.zone.zone_label || '',
            PARCELI2: spatialData.parcel?.parceli2 || null,
            ADDRESS: spatialData.parcel?.address_full || null
          };
        }
        return null;
      })
      .catch(error => {
        console.error('Error fetching zone data from API:', error);
        return null;
      });
  }, []);

  // Helper function for point-in-polygon check using coordinates
  const isPointInPolygonCoords = (point: number[], polygon: number[][]): boolean => {
    const [x, y] = point;
    let inside = false;
    
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const [xi, yi] = polygon[i];
      const [xj, yj] = polygon[j];
      
      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    
    return inside;
  };

  // Expose the zone finder function to parent component (now uses API)
  useEffect(() => {
    if (onZoneFinderReady) {
      if (process.env.NODE_ENV !== 'production') {
        console.log('Exposing API-based zone finder function');
      }
      onZoneFinderReady(findZoneAtCoordinates);
      
      // Also expose to window for debugging
      (window as any).debugZoneFinder = findZoneAtCoordinates;
      if (process.env.NODE_ENV !== 'production') {
        console.log('Zone finder exposed to window.debugZoneFinder for testing');
      }
    }
  }, [onZoneFinderReady, findZoneAtCoordinates]);

  // Display map even when loading - we'll handle the loading state more gracefully
  // This prevents showing a secondary loading screen after the main loading screen

  // Use passed mobile layout state to ensure legend matches sidebar behavior
  useEffect(() => {
    const isLarge = !isMobileLayout; // Use passed prop instead of window size detection
    setIsLargeScreen(isLarge);
    // On large screens (tablet + desktop), show legend by default
    if (isLarge) {
      setIsLegendOpen(true);
    } else {
      setIsLegendOpen(false);
    }
  }, [isMobileLayout]); // React to changes in mobile layout state

  // Click outside to close legend
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isLegendOpen && 
        legendPanelRef.current && 
        !legendPanelRef.current.contains(event.target as Node)
      ) {
        // Check if the click was on the legend toggle button
        const legendToggle = document.querySelector('.legend-toggle');
        if (legendToggle && !legendToggle.contains(event.target as Node)) {
          setIsLegendOpen(false);
        }
      }
    };

    if (isLegendOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLegendOpen]);

  // Memoized map settings to prevent unnecessary remounting during responsive transitions
  const mapSettings = useMemo(() => {
    // Calculate center based on available screen space
    const getResponsiveCenter = (): [number, number] => {
      const baseCenter: [number, number] = [43.7, -79.4];
      
      // Only adjust center for mobile layouts where UI elements affect map space
      if (typeof window !== 'undefined') {
        const width = window.innerWidth;
        const height = window.innerHeight;
        
        // For mobile portrait mode, adjust center slightly north to account for bottom sidebar
        if (width <= 768 && height > width) {
          return [baseCenter[0] + 0.015, baseCenter[1]]; // Slight northward adjustment for mobile UI
        }
        
        // For landscape mode, center normally
        if (height <= 500) {
          return baseCenter;
        }
      }
      
      return baseCenter;
    };
    
    // Use IDENTICAL settings for all screen sizes to prevent MapContainer remounting
    const consistentSettings = {
      center: getResponsiveCenter(), // Responsive center based on UI layout
      zoom: 11, // Same zoom always - no responsive zoom changes
      maxBounds: [[43.2, -80.0], [44.2, -78.8]] as [[number, number], [number, number]], // Same bounds always
      maxBoundsViscosity: 0.3 // Same viscosity always
    };
    
    return consistentSettings;
  }, []); // Empty dependency array - settings never change, preventing remounts

  return (
    <div className="map-container">
      {/* SVG pattern definition for unzoned areas */}
      <svg height="10" width="10" xmlns="http://www.w3.org/2000/svg" version="1.1" style={{ position: 'absolute', width: 0, height: 0 }}>
        <defs>
          <pattern id="unzonedPattern" width="10" height="10" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="10" height="10" fill="#E6E6FA" />
            <line x1="0" y1="0" x2="10" y2="10" style={{ stroke: '#9370DB', strokeWidth: 1 }} />
            <line x1="10" y1="0" x2="0" y2="10" style={{ stroke: '#9370DB', strokeWidth: 1 }} />
          </pattern>
          <pattern id="diagonal-stripes" width="20" height="20" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="20" height="20" fill="rgba(232, 232, 232, 0.3)" />
            <line x1="0" y1="0" x2="0" y2="20" stroke="#666666" strokeWidth="1" strokeOpacity="0.6" />
            <line x1="5" y1="0" x2="5" y2="20" stroke="#666666" strokeWidth="1" strokeOpacity="0.6" />
            <line x1="10" y1="0" x2="10" y2="20" stroke="#666666" strokeWidth="1" strokeOpacity="0.6" />
            <line x1="15" y1="0" x2="15" y2="20" stroke="#666666" strokeWidth="1" strokeOpacity="0.6" />
          </pattern>
        </defs>
      </svg>

      {loading ? (
        <div className="map-loading-overlay">
          <div className="map-loading-content">
            <div className="map-loading-animation">
              <div className="map-loading-bars">
                <div className="map-loading-bar bar-1"></div>
                <div className="map-loading-bar bar-2"></div>
                <div className="map-loading-bar bar-3"></div>
                <div className="map-loading-bar bar-4"></div>
                <div className="map-loading-bar bar-5"></div>
              </div>
            </div>
            <div className="map-loading-text">Preparing map data...</div>
          </div>
        </div>
      ) : null}

      {/* Mobile Progressive Loading Indicator - Removed to avoid double loading screens */}
      
      {error ? <div className="error-message">{error}</div> : null}
      
      <MapContainer 
        center={mapSettings.center} 
        zoom={mapSettings.zoom} 
        className={mapReady ? 'map-visible' : 'map-hidden'}
        style={{ height: '100%', width: '100%' }}
        preferCanvas={true}
        renderer={L.canvas({ padding: 0.5 })}
        zoomAnimation={true}
        markerZoomAnimation={true}
        fadeAnimation={true}
        zoomSnap={0.5}
        zoomDelta={1}
        wheelDebounceTime={20}
        wheelPxPerZoomLevel={80}
        inertia={true}
        inertiaDeceleration={3000}
        inertiaMaxSpeed={2000}
        maxBoundsViscosity={mapSettings.maxBoundsViscosity}
        maxBounds={mapSettings.maxBounds}
        maxZoom={20}
        minZoom={8}
        attributionControl={true}
        zoomControl={false}
      >
        <PremiumBasemap />
        
        {/* Light gray background for unzoned areas - rendered first (lowest) */}
        {cityBoundary && (
          <GeoJSON
            data={cityBoundary}
            style={unzonedAreaStyle}
            interactive={false}
          />
        )}
        
        {/* GeoJSON fallback disabled - vector tiles are working */}
        
        {/* City boundary halo and premium blue outline */}
        {cityBoundary && (
          <GeoJSON
            data={cityBoundary}
            style={cityBoundaryHaloStyle}
            pane="cityBoundaryHaloPane"
            interactive={false}
          />
        )}

        {cityBoundary && (
          <GeoJSON
            data={cityBoundary}
            style={cityBoundaryStyle}
            pane="cityBoundaryPane"
            interactive={false}
          />
        )}
        
        {/* Property lines layer */}
        <PropertyLinesLayer visible={propertyLinesVisible} />
        
        {/* Parcel polygon layer removed */}
        
        {/* Custom positioned zoom control - bottom right with padding */}
        <ZoomControl position="bottomright" />
        
        {/* Property lines are now always visible */}
        
        <MapController />
      </MapContainer>
      
      {/* Legend Toggle Button */}
      {!isLargeScreen && (
        <button 
          className="legend-toggle"
          onClick={() => setIsLegendOpen(!isLegendOpen)}
          aria-label={isLegendOpen ? 'Hide legend' : 'Show legend'}
        >
                  <span>Legend</span>
        </button>
      )}

      {/* Legend Panel */}
      {isLegendOpen && (
        <div ref={legendPanelRef} className={`legend-panel ${isLegendOpen ? 'open' : ''}`}>
          <div className="legend-panel-title">Toronto Zoning</div>
          <div className="legend-panel-content">
            <div className="legend-panel-category">Residential & Mixed Use</div>
            <div className="legend-panel-item">
              <div className="legend-panel-color" style={{ backgroundColor: '#FFD700' }}></div>
              <span>Residential (R)</span>
            </div>
            <div className="legend-panel-item">
              <div className="legend-panel-color" style={{ backgroundColor: '#FFA500' }}></div>
              <span>Residential Apartment</span>
            </div>
            <div className="legend-panel-item">
              <div className="legend-panel-color" style={{ backgroundColor: '#FF0000' }}></div>
              <span>Commercial Residential (CR)</span>
            </div>
            <div className="legend-panel-item">
              <div className="legend-panel-color" style={{ backgroundColor: '#8B4513' }}></div>
              <span>Commercial Residential Employment (CRE)</span>
            </div>
            
            <div className="legend-panel-category">Commercial & Employment</div>
            <div className="legend-panel-item">
              <div className="legend-panel-color" style={{ backgroundColor: '#FF1493' }}></div>
              <span>Commercial (C)</span>
            </div>
            <div className="legend-panel-item">
              <div className="legend-panel-color" style={{ backgroundColor: '#9400D3' }}></div>
              <span>Employment/Industrial (E)</span>
            </div>
            
            <div className="legend-panel-category">Public & Institutional</div>
            <div className="legend-panel-item">
              <div className="legend-panel-color" style={{ backgroundColor: '#1E90FF' }}></div>
              <span>Institutional (I)</span>
            </div>
            <div className="legend-panel-item">
              <div className="legend-panel-color" style={{ backgroundColor: '#32CD32' }}></div>
              <span>Open Space (O)</span>
            </div>
            <div className="legend-panel-item">
              <div className="legend-panel-color" style={{ backgroundColor: '#808080' }}></div>
              <span>Utility (U)</span>
            </div>
            
            <div className="legend-panel-category">Other</div>
            <div className="legend-panel-item">
              <div className="legend-panel-color" style={{ backgroundColor: '#E8E8E8', border: '1px dashed #999999' }}></div>
              <span>Former City By-law Areas</span>
            </div>
            <div className="legend-panel-item">
              <div className="legend-panel-color" style={{ backgroundColor: 'transparent', border: '1px solid #333333' }}></div>
              <span>Property Lines</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TorontoMap;
